
























































































































import { Component, Vue, Prop } from "vue-property-decorator";
import { namespace } from "vuex-class";

/**
 * Helpers
 */
import { iconFaction, thumbnailCharacter, thumbnailClass } from "@/config/image-links";
import classes from "@/config/wow/classes";
import { roles, features } from "@/config/roles";

/**
 * Utils
 */
import { logout } from "@/utils/auth";
import { teamUrl } from "@/utils/url-helpers";

/**
 * API
 */
import { GetOneTeamResponse } from "@/api/teams/get-one-team.response";

import { GetCurrentUserResponse } from "@/api/users/get-current-user.response";

/**
 *  Types
 */
interface SubItemCategory {
	icon?: string;
	avatar?: string;
	link?: string;
	text: string;
	color?: string;
	enabled?: () => boolean;
	items?: SubItemCategory[];
}

interface ItemCategories {
	text: string;
	items: SubItemCategory[];
}

/**
 * VueX Modules
 */
const teamModule = namespace("team");
const profileModule = namespace("profile");


@Component
export default class AppLayout extends Vue {
	/// Injections
	iconFaction = iconFaction;
	teamUrl = teamUrl;

	/// Properties
	@Prop()
	title!: string;

	@Prop({default: false})
	isDark!: boolean;

	/// Attributes

	drawer: boolean | null = null;
	adminDrawer: boolean | null = null;

	/// VueX Modules
	@profileModule.Action("setUserDetails")
	setUserDetails!: (user: GetCurrentUserResponse) => Promise<void>;

	@profileModule.State("userDetails")
	userDetails!: GetCurrentUserResponse | null;

	@teamModule.State("team")
	currentTeam!: GetOneTeamResponse | null;

	get characters(): GetCurrentUserResponse.Character[] {
		if (this.userDetails == null) {
			return [];
		}
		return this.userDetails.characters;
	}

	get itemCategories(): ItemCategories[] {
		const rosterDashboardId = this.$route.params.rosterId ? this.$route.params.rosterId : "";
		const items: ItemCategories[] = [];
		const currentTeam = this.currentTeam;
		if (currentTeam != null) {
			items.push({
				text: "",
				items: [
					{
						icon: "dashboard",
						link: teamUrl(currentTeam, `/dashboard/overview/${rosterDashboardId}`),
						text: this.$tc("menu.dashboard"),
					},
					{
						icon: "format_list_numbered",
						link: teamUrl(currentTeam, `/dashboard/wishes/${rosterDashboardId}`),
						text: this.$tc("menu.wishes"),
					},
					{
						icon: "fas fa-dungeon",
						link: teamUrl(currentTeam, `/dashboard/vault/${rosterDashboardId}`),
						text: this.$tc("menu.great_vault"),
					},
					{
						icon: "fas fa-shield-alt",
						link: teamUrl(currentTeam, `/dashboard/gear/${rosterDashboardId}`),
						text: this.$tc("menu.gear"),
					},
					{
						icon: "calendar_today",
						link: teamUrl(currentTeam, "/calendar"),
						text: this.$tc("menu.calendar"),
					}
				]
			});
			items.push({
				text: this.$tc("menu._categories.team_management"),
				items: [
					{
						icon: "people",
						link: teamUrl(currentTeam, "/rosters"),
						text: this.$tc("menu.manage_rosters"),
						enabled: () => this.hasPermission(roles.MANAGE_ROSTERS)
					},
					{
						icon: "lock",
						link: teamUrl(currentTeam, "/members"),
						text: this.$tc("menu.team_members"),
						enabled: () => this.hasPermission(roles.MANAGE_TEAM)
					},
					{
						icon: "view_list",
						link: teamUrl(currentTeam, "/characters"),
						text: this.$tc("menu.team_characters"),
						enabled: () => this.hasPermission(roles.MANAGE_TEAM)
					},
					{
						icon: "settings",
						link: teamUrl(currentTeam, "/settings"),
						text: this.$tc("menu.team_settings"),
						enabled: () => this.hasPermission(roles.MANAGE_DISCORD) && this.hasFeaturePermission(features.DISCORD)
					}
				]
			});

			items.push({
				text: this.$tc("menu.premium"),
				items: [
					{
						icon: "fas fa-medal",
						link: teamUrl(currentTeam, "/premium"),
						text: this.$tc("menu.premium"),
						enabled: () => currentTeam.premium.premiumLevel == 0
					}
				]
			});
		}


		items.push({
			text: this.$tc("menu._categories.my_characters"),
			items: (this.characters && this.characters.length > 0) ?
				this.characters.filter(x => x.isVisible).map(c => ({
					avatar: c.thumbnail != null ? thumbnailCharacter(c.thumbnail) : thumbnailClass(c.class),
					link: `/characters/${c.id}`,
					text: c.name,
					color: classes[c.class].color
				})) :
				[{
					icon: "person",
					link: "/profile",
					text: this.$tc("menu.claim_character")
				}]
		});


		return items;
	}


	get adminItemCategories(): ItemCategories[] {
		const items: ItemCategories[] = [];
		if (this.isAdmin) {
			items.push({
				text: this.$tc("menu._categories.admin"),
				items: [
					{
						icon: "settings",
						link: "/admin/users",
						text: this.$tc("menu.characters_list"),
					},
					{
						icon: "group",
						link: "/admin/teams",
						text: this.$tc("menu.teams_list"),
					},
					{
						icon: "sync",
						link: "/admin/sync",
						text: this.$tc("menu.sync_queue_list"),
					}
				]
			});
		}

		return items;
	}

	get isAdmin() {
		return this.userDetails && this.userDetails.isAdmin;
	}

	/// Methods
	async created() {
		await this.setUserDetails(this.$route.meta.userProfile);
	}

	isItemCategoryAndChildrenEnabled(item: ItemCategories): boolean {
		return item.items.some(this.isSubItemAndChildrenEnabled);
	}

	isSubItemAndChildrenEnabled(item: SubItemCategory): boolean {
		return (item.enabled == null || item.enabled());
	}
	

	async handleLogout() {
		await logout();
	}

	async handleGuildLogout() {
		await this.$router.push("/");
	}
}
